import { Assignment, ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Grid, IconButton, Typography } from '@mui/material';
import { useState } from 'react';
import { Card, ExternalLink } from '../../../components';
import { CodeBlock } from '../code-block';
import { handleCopyClick } from '../constants';
import { StyleGuidePage } from '../style-guide-page';

export const StyleGuideCards = () => {
  const [isCopied, setIsCopied] = useState<{
    [val: string]: boolean;
  }>({});
  const [isCardExpanded, setIsCardExpanded] = useState<boolean>(true);

  return (
    <StyleGuidePage title="Cards">
      <Grid container mb={2}>
        <Grid item xs={12} lg={8}>
          <Typography mb={1}>
            The card component is built upon the{' '}
            <ExternalLink to="https://mui.com/material-ui/react-card/#basic-card" target="_blank">
              MUI Card
            </ExternalLink>{' '}
            component. This custom component allows for less code, embedding the header and body
            components within it, and custom coloring by implementing a <em>primary</em> colored
            background for the card header. This component also has <em>collapse</em> functionality
            baked into it, so that a card can become an accordion.
          </Typography>
          <Typography mb={1}>
            This component is used for sectioning content, most specifically forms, in order to help
            provide more structure to form fields (i.e. grouping contact info fields in a card
            titled Contact Info). This component is also used for certain sidebar callouts, such as
            news items. This component is not intended for common banded accordion content use,
            unless branding calls for it for some reason.
          </Typography>
          <code>
            Props:
            <br />- ICard
            <br />- title: string <br />- icon?: JSX.Element
            <br />- children: any
            <br />- action?: JSX.ELement
            <br />- isExpanded?: boolean
            <br />- cardTitleClassName?: string
            <br />- headerClassName?: string
            <br />- cardWrapperClassName?: string
            <br />- cardHeaderActionClassName?: string
          </code>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Box mb={1}>
            <Card title="Section Title" icon={<Assignment />}>
              This is the card content.
            </Card>
          </Box>
          <CodeBlock handleCopyClick={e => handleCopyClick(setIsCopied, e)} isCopied={isCopied}>
            &lt;Card title="Section Title" icon=&#123;&lt;Assignment /&gt;&#125;&gt;
            <br />
            &nbsp;&nbsp;&nbsp;This is the card content. <br />
            &lt;/Card&gt;
          </CodeBlock>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box mb={1}>
            <Card
              title="Collapsible Section"
              icon={<Assignment />}
              isExpanded={isCardExpanded}
              action={
                <IconButton
                  onClick={() => setIsCardExpanded(!isCardExpanded)}
                  aria-expanded={isCardExpanded}
                  aria-label="show more"
                >
                  {isCardExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              }
            >
              This is the card content.
            </Card>
          </Box>
          <CodeBlock handleCopyClick={e => handleCopyClick(setIsCopied, e)} isCopied={isCopied}>
            &lt;Card
            <br />
            &nbsp;&nbsp;&nbsp;title="Collapsible Section"
            <br />
            &nbsp;&nbsp;&nbsp;icon=&#123;&lt;Assignment /&gt;&#125;
            <br />
            &nbsp;&nbsp;&nbsp;isExpanded=&#123;isCardExpanded&#125;
            <br />
            &nbsp;&nbsp;&nbsp;action=&#123;
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;IconButton
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;onClick=&#123;() =&gt;
            setIsCardExpanded(!isCardExpanded)&#125;
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;aria-expanded=&#123;isCardExpanded&#125;
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;aria-label="show more"
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&gt;
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&#123;isCardExpanded ? &lt;ExpandLess /&gt; :
            &lt;ExpandMore /&gt;&#125;
            <br />
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&lt;/IconButton&gt;
            <br />
            &nbsp;&nbsp;&nbsp;&#125;
            <br />
            &gt;
            <br />
            &nbsp;&nbsp;&nbsp;This is the card content.
            <br />
            &lt;/Card&gt;
          </CodeBlock>
        </Grid>
      </Grid>
    </StyleGuidePage>
  );
};
